import { SlotShape } from '../models/loyalty-card.model';

/* -------------------------------------------------------------------------- */
/*            ANCHOR Rejilla del reverso (02-decisiones-ui.md, punto 1)        */
/* -------------------------------------------------------------------------- */
/* Tarjeta base 392×224 (1.75:1), área segura de 20 px.                        */
/* N <= 14 → 2 filas; N >= 15 → 3 filas. cols = ceil(N / filas).              */
/* s = min(58, floor(anchoÚtil / (cols + 0.27·(cols−1)))), separación 0.27·s. */
/* Además se limita por alto para que quepa el texto debajo de la rejilla.    */

export const CARD_W = 392;
export const CARD_H = 224;
const SAFE = 20;
const TEXT_BLOCK = 48; // 2 líneas de texto (13/18) + separación

export interface IStampGrid {
  rows: number[];   // casillas por fila
  size: number;     // lado de la casilla (px de la tarjeta base)
  gap: number;      // separación entre casillas
  top: number;      // y inicial de la rejilla
  textTop: number;  // y del texto
}

export function computeStampGrid(n: number, hasText = true): IStampGrid {
  const total = Math.max(4, Math.min(20, Math.round(n || 4)));
  const rowsCount = total <= 14 ? 2 : 3;
  const cols = Math.ceil(total / rowsCount);
  const usableW = CARD_W - SAFE * 2;
  const usableH = CARD_H - SAFE * 2 - (hasText ? TEXT_BLOCK : 0);
  const byWidth = Math.floor(usableW / (cols + 0.27 * (cols - 1)));
  const byHeight = Math.floor(usableH / (rowsCount + 0.27 * (rowsCount - 1)));
  const size = Math.min(58, byWidth, byHeight);
  const gap = Math.max(6, Math.round(size * 0.27));

  const rows: number[] = [];
  let left = total;
  for (let r = 0; r < rowsCount; r++) {
    const c = Math.min(cols, left);
    rows.push(c);
    left -= c;
  }

  const gridH = rowsCount * size + (rowsCount - 1) * gap;
  const blockH = gridH + (hasText ? TEXT_BLOCK - 8 : 0);
  const top = Math.max(SAFE, Math.round((CARD_H - blockH) / 2));
  return { rows, size, gap, top, textTop: top + gridH + 14 };
}

/* -------------------------------------------------------------------------- */
/*                 ANCHOR Formas de casilla (viewBox 0 0 56 56)                */
/* -------------------------------------------------------------------------- */

const SCALLOP = 'M28 0L28.73 0.03L29.46 0.12L30.18 0.28L30.89 0.49L31.59 0.76L32.26 1.08L32.92 1.46L33.55 1.88L34.16 2.34L34.74 2.85L35.29 3.38L35.82 3.95L36.31 4.53L36.78 5.14L37.22 5.75L37.96 5.63L38.72 5.53L39.48 5.47L40.25 5.44L41.02 5.45L41.79 5.5L42.54 5.6L43.29 5.75L44.02 5.95L44.73 6.2L45.41 6.5L46.06 6.85L46.68 7.26L47.26 7.71L47.8 8.2L48.29 8.74L48.74 9.32L49.15 9.94L49.5 10.59L49.8 11.27L50.05 11.98L50.25 12.71L50.4 13.46L50.5 14.21L50.55 14.98L50.56 15.75L50.53 16.52L50.47 17.28L50.37 18.04L50.25 18.78L50.86 19.22L51.47 19.69L52.05 20.18L52.62 20.71L53.15 21.26L53.66 21.84L54.12 22.45L54.54 23.08L54.92 23.74L55.24 24.41L55.51 25.11L55.72 25.82L55.88 26.54L55.97 27.27L56 28L55.97 28.73L55.88 29.46L55.72 30.18L55.51 30.89L55.24 31.59L54.92 32.26L54.54 32.92L54.12 33.55L53.66 34.16L53.15 34.74L52.62 35.29L52.05 35.82L51.47 36.31L50.86 36.78L50.25 37.22L50.37 37.96L50.47 38.72L50.53 39.48L50.56 40.25L50.55 41.02L50.5 41.79L50.4 42.54L50.25 43.29L50.05 44.02L49.8 44.73L49.5 45.41L49.15 46.06L48.74 46.68L48.29 47.26L47.8 47.8L47.26 48.29L46.68 48.74L46.06 49.15L45.41 49.5L44.73 49.8L44.02 50.05L43.29 50.25L42.54 50.4L41.79 50.5L41.02 50.55L40.25 50.56L39.48 50.53L38.72 50.47L37.96 50.37L37.22 50.25L36.78 50.86L36.31 51.47L35.82 52.05L35.29 52.62L34.74 53.15L34.16 53.66L33.55 54.12L32.92 54.54L32.26 54.92L31.59 55.24L30.89 55.51L30.18 55.72L29.46 55.88L28.73 55.97L28 56L27.27 55.97L26.54 55.88L25.82 55.72L25.11 55.51L24.41 55.24L23.74 54.92L23.08 54.54L22.45 54.12L21.84 53.66L21.26 53.15L20.71 52.62L20.18 52.05L19.69 51.47L19.22 50.86L18.78 50.25L18.04 50.37L17.28 50.47L16.52 50.53L15.75 50.56L14.98 50.55L14.21 50.5L13.46 50.4L12.71 50.25L11.98 50.05L11.27 49.8L10.59 49.5L9.94 49.15L9.32 48.74L8.74 48.29L8.2 47.8L7.71 47.26L7.26 46.68L6.85 46.06L6.5 45.41L6.2 44.73L5.95 44.02L5.75 43.29L5.6 42.54L5.5 41.79L5.45 41.02L5.44 40.25L5.47 39.48L5.53 38.72L5.63 37.96L5.75 37.22L5.14 36.78L4.53 36.31L3.95 35.82L3.38 35.29L2.85 34.74L2.34 34.16L1.88 33.55L1.46 32.92L1.08 32.26L0.76 31.59L0.49 30.89L0.28 30.18L0.12 29.46L0.03 28.73L0 28L0.03 27.27L0.12 26.54L0.28 25.82L0.49 25.11L0.76 24.41L1.08 23.74L1.46 23.08L1.88 22.45L2.34 21.84L2.85 21.26L3.38 20.71L3.95 20.18L4.53 19.69L5.14 19.22L5.75 18.78L5.63 18.04L5.53 17.28L5.47 16.52L5.44 15.75L5.45 14.98L5.5 14.21L5.6 13.46L5.75 12.71L5.95 11.98L6.2 11.27L6.5 10.59L6.85 9.94L7.26 9.32L7.71 8.74L8.2 8.2L8.74 7.71L9.32 7.26L9.94 6.85L10.59 6.5L11.27 6.2L11.98 5.95L12.71 5.75L13.46 5.6L14.21 5.5L14.98 5.45L15.75 5.44L16.52 5.47L17.28 5.53L18.04 5.63L18.78 5.75L19.22 5.14L19.69 4.53L20.18 3.95L20.71 3.38L21.26 2.85L21.84 2.34L22.45 1.88L23.08 1.46L23.74 1.08L24.41 0.76L25.11 0.49L25.82 0.28L26.54 0.12L27.27 0.03L28 0Z';

function roundedPolygon(points: [number, number][], radius: number): string {
  const n = points.length;
  let d = '';
  for (let i = 0; i < n; i++) {
    const p0 = points[(i - 1 + n) % n];
    const p1 = points[i];
    const p2 = points[(i + 1) % n];
    const v1 = [p0[0] - p1[0], p0[1] - p1[1]];
    const v2 = [p2[0] - p1[0], p2[1] - p1[1]];
    const l1 = Math.hypot(v1[0], v1[1]);
    const l2 = Math.hypot(v2[0], v2[1]);
    const r = Math.min(radius, l1 / 2, l2 / 2);
    const a = [p1[0] + (v1[0] / l1) * r, p1[1] + (v1[1] / l1) * r];
    const b = [p1[0] + (v2[0] / l2) * r, p1[1] + (v2[1] / l2) * r];
    d += (i === 0 ? 'M' : 'L') + a[0].toFixed(2) + ' ' + a[1].toFixed(2);
    d += 'Q' + p1[0].toFixed(2) + ' ' + p1[1].toFixed(2) + ' ' + b[0].toFixed(2) + ' ' + b[1].toFixed(2);
  }
  return d + 'Z';
}

function starPoints(spikes: number, inner: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? 28 : 28 * inner;
    const ang = (Math.PI / spikes) * i - Math.PI / 2;
    pts.push([28 + r * Math.cos(ang), 28 + r * Math.sin(ang)]);
  }
  return pts;
}

function hexPoints(): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const ang = (Math.PI / 3) * i - Math.PI / 2;
    pts.push([28 + 28 * Math.cos(ang), 28 + 28 * Math.sin(ang)]);
  }
  return pts;
}

const SHAPE_PATHS: Record<SlotShape, string> = {
  circle: 'M28 0A28 28 0 1 1 27.99 0Z',
  flower: SCALLOP,
  square: 'M15.68 0H40.32A15.68 15.68 0 0 1 56 15.68V40.32A15.68 15.68 0 0 1 40.32 56H15.68A15.68 15.68 0 0 1 0 40.32V15.68A15.68 15.68 0 0 1 15.68 0Z',
  seal: roundedPolygon(starPoints(12, 0.84), 2.24),
  hexagon: roundedPolygon(hexPoints(), 7.84),
};

export function slotShapePath(shape: SlotShape | string | null | undefined): string {
  return SHAPE_PATHS[(shape as SlotShape)] ?? SHAPE_PATHS.circle;
}
